// The questions on /specialists, also sent to search engines and AI tools as FAQPage
// data. Each answer follows the Implementation Specialist terms. Keep them to what the
// terms say, and keep pricing and the pilot's cost out: those stay in the terms.
import { SHARE, SHARE_MONTHS } from "./SpecialistModel";

export const FAQ_GROUPS = [
  {
    title: "The role",
    items: [
      {
        q: "What is BossDrop?",
        a: "An AI coordination layer for ops teams. It reads the work across chat, email, task tools and calendars, then chases status, escalates blockers, drafts and assigns tasks, proposes decision meetings and sends the executive brief, with every action logged. It runs inside the tools a team already uses.",
      },
      {
        q: "What does an Implementation Specialist do?",
        a: "You bring a company and help run a 45-day pilot with one of its teams: connecting the tools with the client's permission, scoping the team, agreeing the success measures, holding the weekly check-in and preparing the day 45 readout. On your early rollouts you do this together with Eric. In time, you run them on your own.",
      },
      {
        q: "Who should apply?",
        a: "Fractional COOs, chiefs of staff, operators and consultants who can bring their own clients and want to be hands-on with a rollout. If you've rolled out Asana, monday.com, Jira or something like them, you'll recognize much of the job.",
      },
      {
        q: "Do I need to bring my own clients?",
        a: "Yes. BossDrop doesn't supply leads. A good first client is one team of 10 to 30 people on Slack or Teams, with a leader who can say yes to a pilot.",
      },
      {
        q: "Do I have to run the rollout on my own?",
        a: "No. Early on, you and Eric run it together. If you'd rather only make the introduction, tell Eric before the pilot starts and Eric runs it. Your share stays the same.",
      },
      {
        q: "Do I need a certification or training?",
        a: "No certification, and there's no course to sit through today. You learn by running your early rollouts with Eric, who also hosts every demo, and you get a private Slack channel with Eric for anything that comes up.",
      },
    ],
  },
  {
    title: "The money",
    items: [
      {
        q: "How much can I earn?",
        a: "There's no guaranteed amount, and BossDrop is too early to have specialist results to quote. What you earn depends on what you charge, how many clients you bring and what those clients pay. Try your own numbers in the model on this page.",
      },
      {
        q: "Can I set my own fees?",
        a: "Yes. You set what you charge for your work and keep all of it. BossDrop takes no cut and doesn't collect it. Make clear to your client that the fee is yours, not BossDrop's.",
      },
      {
        q: `How does the ${SHARE} work?`,
        a: `You earn ${SHARE} of what BossDrop actually receives from the company for service in its first ${SHARE_MONTHS} months as a paying customer, counted from its first payment. That first payment counts, and so does any team the company adds in those ${SHARE_MONTHS} months. Taxes, refunds, credits and chargebacks don't, and a later refund comes off your next payout.`,
      },
      {
        q: "What does BossDrop charge my client?",
        a: "BossDrop quotes each company itself, priced per team rather than per seat, so there's no list price to show here. Your terms explain how the pilot and pricing work, and your share follows what the client actually pays.",
      },
      {
        q: "When do I get paid?",
        a: "By the 15th of the month after each customer payment clears, by bank transfer in US dollars. BossDrop pays nothing for an introduction, a demo or a pilot, so with the pilot first, expect about three months or more before your first share.",
      },
      {
        q: "Does it cost anything to join?",
        a: "No. Applying and joining cost you nothing, and Eric reads every application.",
      },
    ],
  },
  {
    title: "The fine print",
    items: [
      {
        q: "How is a company tracked to me?",
        a: "Register it in your dashboard, book a demo through your link, email Eric or message your Slack channel, naming the company and the person. You get an answer within 5 business days. One company has one specialist, and the earliest registration wins. A link is remembered for 90 days in the browser that opened it, so the dashboard is the sure way.",
      },
      {
        q: "How long does a company stay mine?",
        a: "Once confirmed, it stays yours for 6 months to reach a signed pilot or paid agreement, and 12 months to reach a first payment.",
      },
      {
        q: "Can I bring a company I already advise?",
        a: "Yes, as a contractor or adviser, including under a title like fractional COO. Tell a decision maker other than yourself, in writing, before the company signs anything, and copy Eric. You can't be the one who approves the purchase. Employees, directors, officers on its payroll and owners of more than 5% can't earn on that company.",
      },
      {
        q: "Do I have to tell people BossDrop pays me?",
        a: "Yes, every time, up front. In a public post it goes in the post, next to your link. Something like: \"I'm a paid implementation specialist for BossDrop.\"",
      },
      {
        q: "Am I a BossDrop employee?",
        a: "No. You're an independent contractor. You run your own work, pay your own costs and taxes, and send a W-9 (or a W-8 outside the US) before your first payout. You can't sign anything for BossDrop.",
      },
      {
        q: "What if either of us wants to stop?",
        a: `Either side can end the agreement on 30 days' written notice, and email counts. A share on a confirmed company keeps running to the end of its ${SHARE_MONTHS} months, unless the introduction lapsed or the agreement ended because the ground rules were broken. Terms can only change for future introductions, with 30 days' notice, and you can still log in to see your earnings.`,
      },
    ],
  },
];

export const FAQ_ITEMS = FAQ_GROUPS.flatMap((group) => group.items);

export const FAQ_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_ITEMS.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
};
